import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { LanguageContext } from "../context/LanguageContext";
import {
  apiClient,
  getInactiveHoursRep1,
  getReportedHoursRep1,
  getActivityTypeBreakdown,
  getActivityTypeTimeSeries,
} from "../api";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend,
  CategoryScale, LinearScale, BarElement, Title,
} from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const COLORS = ["#4CAF50","#FF9F40","#36A2EB","#FF6384","#FFCD56","#9C27B0","#00BCD4","#F44336","#8BC34A","#FF5722"];
const CHART_BG = "rgb(46,46,46)";
const GRANULARITIES = ["day", "week", "month", "year"];

const chartBgPlugin = {
  id: "chartBgPlugin",
  beforeDraw: (chart) => {
    const { ctx } = chart;
    ctx.save();
    ctx.fillStyle = CHART_BG;
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  },
};

// ISO week key: "2026-W15"
function getIsoWeekKey(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const thursday = new Date(d);
  thursday.setDate(d.getDate() - (d.getDay() + 6) % 7 + 3);
  const jan4 = new Date(thursday.getFullYear(), 0, 4);
  const week = 1 + Math.round(((thursday - jan4) / 86400000 - 3 + (jan4.getDay() + 6) % 7) / 7);
  return `${thursday.getFullYear()}-W${String(week).padStart(2, "0")}`;
}

function getPeriodKey(dateStr, granularity) {
  if (granularity === "week")  return getIsoWeekKey(dateStr);
  if (granularity === "month") return dateStr.slice(0, 7);
  if (granularity === "year")  return dateStr.slice(0, 4);
  return dateStr;
}

function aggregateSeries(dailySeries, granularity) {
  if (granularity === "day") return dailySeries;
  const map = new Map();
  for (const d of dailySeries) {
    const key = getPeriodKey(d.period, granularity);
    if (!map.has(key)) map.set(key, { period: key, reported_hours: 0, inactive_hours: 0 });
    const e = map.get(key);
    e.reported_hours  = Math.round((e.reported_hours  + d.reported_hours)  * 100) / 100;
    e.inactive_hours  = Math.round((e.inactive_hours  + d.inactive_hours)  * 100) / 100;
  }
  return Array.from(map.values()).sort((a, b) => a.period.localeCompare(b.period));
}

// ─────────────────────────────────────────────────────────────────────────────

const ActivityTypeRep1 = ({ id_c: idCProp }) => {
  const { t } = useContext(LanguageContext);

  const company = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("company")) || {}; } catch { return {}; }
  }, []);

  const id_c = idCProp ?? company.id_c ?? null;
  const storeKeyMachines = `act_type_rep_machine_codes_${id_c || "na"}`;

  const [dateFrom, setDateFrom] = useState(() => localStorage.getItem("act_type_rep_date_from") || "");
  const [dateTo,   setDateTo]   = useState(() => localStorage.getItem("act_type_rep_date_to")   || "");
  const [allMachines, setAllMachines] = useState([]);
  const [machineCodes, setMachineCodes] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storeKeyMachines) || "[]"); } catch { return []; }
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [q, setQ] = useState("");
  const dropdownRef = useRef(null);

  const [reportedHours, setReportedHours] = useState(null);
  const [inactiveHours, setInactiveHours] = useState(null);
  const [byType,        setByType]        = useState([]);
  const [dailySeries,   setDailySeries]   = useState([]);
  const [tsError,       setTsError]       = useState(false);
  const [granularity,   setGranularity]   = useState("day");
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);

  // Default: mes actual
  useEffect(() => {
    if (dateFrom && dateTo) return;
    const pad = (n) => String(n).padStart(2, "0");
    const now = new Date();
    const first = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`;
    const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    if (!dateFrom) { setDateFrom(first); localStorage.setItem("act_type_rep_date_from", first); }
    if (!dateTo)   { setDateTo(today);   localStorage.setItem("act_type_rep_date_to",   today); }
  }, []);

  // Cargar máquinas
  useEffect(() => {
    if (!id_c) return;
    apiClient.get(`/company/${id_c}/machines`).then(({ data }) => {
      const list = (data?.machines || []).map((m) => ({ cod_maq: m.cod_maq, name_m: m.name_m }));
      setAllMachines(list);
      if (!machineCodes.length && list.length) {
        const all = list.map((m) => m.cod_maq);
        setMachineCodes(all);
        localStorage.setItem(storeKeyMachines, JSON.stringify(all));
      }
    }).catch(console.error);
  }, [id_c]);

  // Cerrar picker al clic fuera
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setPickerOpen(false);
    };
    if (pickerOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pickerOpen]);

  const filteredMachines = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? allMachines.filter((m) => m.cod_maq.toLowerCase().includes(s)) : allMachines;
  }, [q, allMachines]);

  const allSelected  = machineCodes.length && machineCodes.length === allMachines.length;
  const someSelected = machineCodes.length > 0 && machineCodes.length < allMachines.length;

  const toggleAll = () => {
    const next = allSelected ? [] : allMachines.map((m) => m.cod_maq);
    setMachineCodes(next);
    localStorage.setItem(storeKeyMachines, JSON.stringify(next));
  };
  const toggleOne = (code) => {
    const s = new Set(machineCodes);
    s.has(code) ? s.delete(code) : s.add(code);
    const next = Array.from(s);
    setMachineCodes(next);
    localStorage.setItem(storeKeyMachines, JSON.stringify(next));
  };

  const labelPicker = useMemo(() => {
    if (!allMachines.length) return t("atr_loading");
    if (!machineCodes.length) return t("atr_noMachines");
    if (allSelected) return t("atr_allMachines");
    return `${machineCodes.length} ${t("atr_machinesLabel")}`;
  }, [allMachines.length, machineCodes.length, allSelected, t]);

  const onApply = async () => {
    if (!id_c || !dateFrom || !dateTo || dateFrom > dateTo) return;
    const codes = machineCodes.length ? machineCodes : allMachines.map((m) => m.cod_maq);
    setLoading(true);
    setError(null);
    try {
      const [repR, inactR, breakR, tsR] = await Promise.allSettled([
        getReportedHoursRep1({ id_c, dateFrom, dateTo, machineCodes: codes }),
        getInactiveHoursRep1({ id_c, dateFrom, dateTo, machineCodes: codes }),
        getActivityTypeBreakdown({ id_c, dateFrom, dateTo, machineCodes: codes }),
        getActivityTypeTimeSeries({ id_c, dateFrom, dateTo, machineCodes: codes }),
      ]);
      setReportedHours(repR.status   === "fulfilled" ? Number(repR.value?.total_reported_hours  || 0) : 0);
      setInactiveHours(inactR.status === "fulfilled" ? Number(inactR.value?.total_inactive_hours || 0) : 0);
      setByType(breakR.status        === "fulfilled" ? (breakR.value?.by_type || []) : []);
      if (tsR.status === "fulfilled") {
        setDailySeries(tsR.value?.series || []);
        setTsError(false);
      } else {
        setDailySeries([]);
        setTsError(true);
      }
    } catch (err) {
      setError(t("atr_error"));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const hasData = reportedHours !== null;

  const total    = (reportedHours || 0) + (inactiveHours || 0);
  const repPct   = total > 0 ? ((reportedHours  / total) * 100).toFixed(1) : 0;
  const inactPct = total > 0 ? ((inactiveHours  / total) * 100).toFixed(1) : 0;

  // ── Doughnut ──────────────────────────────────────────────────────
  const donutData = {
    labels: [t("atr_reportedHours"), t("atr_inactiveHours")],
    datasets: [{
      data: [reportedHours || 0, inactiveHours || 0],
      backgroundColor: ["#0084ff", "#FF9F40"],
      borderWidth: 2,
      borderColor: ["#0084ff", "#FF9F40"],
    }],
  };

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { color: "#c6c6c6", padding: 16 } },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const val = ctx.raw;
            const pct = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
            return ` ${val.toFixed(2)} h (${pct}${t("atr_ofTotal")})`;
          },
        },
      },
    },
  };

  // ── Bar por tipo ──────────────────────────────────────────────────
  const barData = {
    labels: byType.map((item) => item.label || item.id_activity),
    datasets: [{
      label: t("atr_inactiveHoursLabel"),
      data: byType.map((item) => Number(item.hours)),
      backgroundColor: byType.map((_, i) => COLORS[i % COLORS.length]),
      borderRadius: 4,
    }],
  };

  const barOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const hours = ctx.raw;
            const pct = total > 0 ? ((hours / total) * 100).toFixed(1) : 0;
            return ` ${hours.toFixed(2)} h  (${pct}${t("atr_ofTotal")})`;
          },
        },
      },
    },
    scales: {
      x: { ticks: { color: "#c6c6c6" }, grid: { color: "#444" } },
      y: { ticks: { color: "#c6c6c6" }, grid: { color: "#444" } },
    },
  };

  const barHeight = Math.max(200, byType.length * 48);

  // ── Timeseries (stacked vertical bar) ────────────────────────────
  const aggregated = useMemo(() => aggregateSeries(dailySeries, granularity), [dailySeries, granularity]);

  const stackedData = {
    labels: aggregated.map((d) => d.period),
    datasets: [
      {
        label: t("atr_reportedHours"),
        data: aggregated.map((d) => d.reported_hours),
        backgroundColor: "#0084ff",
        stack: "s",
        borderRadius: 2,
      },
      {
        label: t("atr_inactiveHours"),
        data: aggregated.map((d) => d.inactive_hours),
        backgroundColor: "#FF9F40",
        stack: "s",
        borderRadius: 2,
      },
    ],
  };

  const stackedOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", labels: { color: "#c6c6c6", padding: 16 } },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.dataset.label}: ${Number(ctx.raw).toFixed(2)} h`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: { color: "#c6c6c6", maxRotation: 45, autoSkip: true, maxTicksLimit: 24 },
        grid: { color: "#444" },
      },
      y: {
        stacked: true,
        ticks: { color: "#c6c6c6" },
        grid: { color: "#444" },
      },
    },
  };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="box1">
      <div className="box13">

        {/* Filtros */}
        <div style={{ display: "flex", alignItems: "center", padding: "12px 0", gap: 0, background: "rgb(46,46,46)" }}>

          {/* Logo — 1/3 */}
          <div style={{ width: "33%", display: "flex", justifyContent: "center", alignItems: "center", flexShrink: 0 }}>
            <img className="logo6" src={company?.logo_c} alt="Logo" />
          </div>

          {/* Controles — 2/3 */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12, paddingRight: 20 }}>

            {/* Fila fechas */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span className="text3" style={{ whiteSpace: "nowrap", minWidth: 80 }}>{t("atr_dateFrom")}</span>
              <input type="date" style={{ flex: 1, textAlign: "center" }}
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); localStorage.setItem("act_type_rep_date_from", e.target.value); }}
              />
              <span className="text3" style={{ whiteSpace: "nowrap", minWidth: 60, textAlign: "center" }}>{t("atr_dateTo")}</span>
              <input type="date" style={{ flex: 1, textAlign: "center" }}
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); localStorage.setItem("act_type_rep_date_to", e.target.value); }}
              />
            </div>

            {/* Fila máquinas + apply */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span className="text3" style={{ whiteSpace: "nowrap", minWidth: 80 }}>{t("atr_machines")}</span>
              <div ref={dropdownRef} style={{ flex: 1, minWidth: 180, position: "relative" }}>
                <button type="button" className="btn"
                  onClick={() => setPickerOpen((o) => !o)}
                  style={{ width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", background: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {labelPicker}
                </button>
                {pickerOpen && (
                  <div style={{ position: "absolute", zIndex: 30, top: "calc(100% + 6px)", left: 0, width: "100%",
                    background: "#fff", border: "1px solid #ddd", borderRadius: 8, padding: 8, boxShadow: "0 8px 24px rgba(0,0,0,.15)" }}>
                    <input type="text" placeholder={t("atr_search")} value={q} onChange={(e) => setQ(e.target.value)}
                      style={{ width: "100%", marginBottom: 8, padding: 6, borderRadius: 6, border: "1px solid #ddd" }} />
                    <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
                      <input id="chk-all-atr" type="checkbox" checked={!!allSelected}
                        ref={(el) => { if (el) el.indeterminate = !!someSelected; }}
                        onChange={toggleAll} style={{ marginRight: 6 }} />
                      <label htmlFor="chk-all-atr">{t("atr_all")}</label>
                    </div>
                    <div style={{ maxHeight: 260, overflowY: "auto" }}>
                      {filteredMachines.map((m) => (
                        <label key={m.cod_maq} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", cursor: "pointer" }}>
                          <input type="checkbox" checked={machineCodes.includes(m.cod_maq)} onChange={() => toggleOne(m.cod_maq)} />
                          <span style={{ fontSize: 14 }}>{m.cod_maq}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <button onClick={onApply} className="button5" style={{ minWidth: 100, whiteSpace: "nowrap" }}>
                {loading ? t("atr_calculating") : t("atr_apply")}
              </button>
            </div>

          </div>
        </div>

        {error && <div style={{ color: "#c00", padding: "8px 20px" }}>{error}</div>}

        {hasData && (
          <div style={{ padding: "0 20px" }}>
            {/* KPIs */}
            <div className="kpi-row" style={{ marginTop: 16 }}>
              <div className="card">
                <h1>{reportedHours?.toFixed(2)}</h1>
                <p>{t("atr_reportedHours")}</p>
                <small style={{ color: "#0084ff" }}>{repPct}{t("atr_ofTotal")}</small>
              </div>
              <div className="card">
                <h1>{inactiveHours?.toFixed(2)}</h1>
                <p>{t("atr_inactiveHours")}</p>
                <small style={{ color: "#FF9F40" }}>{inactPct}{t("atr_ofTotal")}</small>
              </div>
              <div className="card">
                <h1>{total?.toFixed(2)}</h1>
                <p>{t("atr_totalHours")}</p>
                <small style={{ color: "#c6c6c6" }}>{t("atr_selectedPeriod")}</small>
              </div>
            </div>

            {/* Gráficas donut + barras por tipo */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 24 }}>

              <div style={{ background: CHART_BG, borderRadius: 12, padding: 20, border: "1px solid #444" }}>
                <p style={{ color: "#c6c6c6", fontWeight: 600, fontSize: 15, marginBottom: 16, borderBottom: "1px solid #555", paddingBottom: 10 }}>
                  {t("atr_chartTitle1")}
                </p>
                <div style={{ height: 300 }}>
                  <Doughnut data={donutData} options={donutOptions} plugins={[chartBgPlugin]} />
                </div>
                <div style={{ marginTop: 16, display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
                  <span style={{ color: "#0084ff", fontSize: 13 }}>● {t("atr_legendReported")}: {reportedHours?.toFixed(2)} h ({repPct}%)</span>
                  <span style={{ color: "#FF9F40", fontSize: 13 }}>● {t("atr_legendInactive")}: {inactiveHours?.toFixed(2)} h ({inactPct}%)</span>
                </div>
              </div>

              <div style={{ background: CHART_BG, borderRadius: 12, padding: 20, border: "1px solid #444" }}>
                <p style={{ color: "#c6c6c6", fontWeight: 600, fontSize: 15, marginBottom: 16, borderBottom: "1px solid #555", paddingBottom: 10 }}>
                  {t("atr_chartTitle2")}
                </p>
                {byType.length === 0
                  ? <p style={{ color: "#888", textAlign: "center", marginTop: 40 }}>{t("atr_noActivities")}</p>
                  : <div style={{ height: barHeight }}>
                      <Bar data={barData} options={barOptions} plugins={[chartBgPlugin]} />
                    </div>
                }
              </div>

            </div>

            {/* Gráfica de series de tiempo */}
            <div style={{ background: CHART_BG, borderRadius: 12, padding: 20, border: "1px solid #444", marginTop: 24 }}>

              {/* Título + toggles de granularidad */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16, borderBottom: "1px solid #555", paddingBottom: 10 }}>
                <p style={{ color: "#c6c6c6", fontWeight: 600, fontSize: 15, margin: 0 }}>
                  {t("atr_timeseriesTitle")}
                </p>
                <div style={{ display: "flex", gap: 6 }}>
                  {GRANULARITIES.map((g) => (
                    <button
                      key={g}
                      onClick={() => setGranularity(g)}
                      style={{
                        padding: "4px 14px",
                        borderRadius: 6,
                        border: "1px solid #555",
                        cursor: "pointer",
                        fontSize: 13,
                        background: granularity === g ? "#0084ff" : "rgb(60,60,60)",
                        color: granularity === g ? "#fff" : "#c6c6c6",
                        fontWeight: granularity === g ? 600 : 400,
                        transition: "background 0.15s",
                      }}
                    >
                      {t(`atr_gran_${g}`)}
                    </button>
                  ))}
                </div>
              </div>

              {tsError
                ? <p style={{ color: "#c00", textAlign: "center", marginTop: 40 }}>{t("atr_error")}</p>
                : aggregated.length === 0
                  ? <p style={{ color: "#888", textAlign: "center", marginTop: 40 }}>{t("atr_noActivities")}</p>
                  : <div style={{ height: 320 }}>
                    <Bar data={stackedData} options={stackedOptions} plugins={[chartBgPlugin]} />
                  </div>
              }
            </div>

          </div>
        )}

        {!hasData && !loading && (
          <div style={{ textAlign: "center", color: "#888", padding: 40 }}>
            {t("atr_selectPeriod")}
          </div>
        )}

      </div>
    </div>
  );
};

export default ActivityTypeRep1;
